import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { settings } from '@/config';
import type { Renderer } from '@/renderer/Renderer';
import type { SizesState } from '@/types';

/** Only objects with this layer enabled are rendered into the bloom source. Everything else —
 *  sky, baked textures, water, vegetation — is literally not drawn, so it CANNOT be affected. */
export const BLOOM_LAYER = 1;

/**
 * Glow on the emissive objects ONLY (2026-07-18, user: "I want that light only to stones and that
 * light portal — not other should get affected colors because of postprocessing").
 *
 * WHY NOT THE NORMAL COMPOSER: it re-processes the WHOLE frame — its `OutputPass` re-applies tone
 * mapping + colour conversion to every pixel, including the sky gradient and the baked atlases
 * (both authored `toneMapped:false`). There is no way to run the frame through the composer and
 * have them come out unchanged. So the base image must bypass it entirely.
 *
 * WHAT THIS DOES: the scene renders DIRECTLY to the screen (sky + bake pixel-perfect), then ONLY
 * the {@link BLOOM_LAYER} objects (portal light + halo, the stones' hover glow, the orb) are
 * re-rendered on black, blurred by a real `UnrealBloomPass`, and the resulting glow is ADDITIVELY
 * composited on top. Nothing else is even drawn into the bloom source, so the sky and every
 * texture are mathematically untouchable — the base frame is never sampled, tone-mapped, or
 * re-encoded, only *added to* where those few objects glow.
 *
 * CHEAP BY DESIGN (must run on weak / software-WebGL devices): the bloom renders at
 * `resolutionScale` of the canvas (bloom is blurry, so half-res is visually free) and the source
 * contains only a handful of objects.
 */
export class SelectiveBloom {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly bloomComposer: EffectComposer;
  private readonly bloomPass: UnrealBloomPass;

  // Fullscreen additive overlay that lays the bloom texture over the screen.
  private readonly overlayScene = new THREE.Scene();
  private readonly overlayCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly overlayMat: THREE.ShaderMaterial;

  /** Heavy subtrees hidden during the bloom pass to keep its scene walk cheap — see
   *  {@link setTraversalSkip}. Purely a CPU optimisation; the rendered pixels are identical. */
  private skip: THREE.Object3D[] = [];
  private readonly skipVisible: boolean[] = [];

  /** OCCLUDER SUPPORT (see {@link render}). Non-emissive meshes are swapped to a black stand-in
   *  for the bloom pass so they write depth and mask emitters behind them. `darkCache` maps a
   *  source material → its black twin (built once); `saved` holds the real materials to restore. */
  private readonly darkCache = new Map<string, THREE.Material>();
  private readonly saved = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
  /** Meshes hidden for the duration of the pass rather than darkened — see `darken`. */
  private readonly hidden: THREE.Mesh[] = [];
  /** Layer mask used to ask "is this an emitter?" without allocating per test. */
  private readonly bloomLayers = new THREE.Layers();


  constructor(
    renderer: Renderer,
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.Camera,
    sizes: SizesState,
  ) {
    this.renderer = renderer.instance;
    this.bloomLayers.set(BLOOM_LAYER);
    const cfg = settings.selectiveBloom;

    const rt = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      samples: 0,
    });
    this.bloomComposer = new EffectComposer(this.renderer, rt);
    this.bloomComposer.renderToScreen = false;

    // RenderPass with an explicit BLACK clear — so the (unrendered) non-emissive areas are black
    // and contribute no glow (the renderer's own clear colour is a light blue that would wash
    // the whole overlay).
    const renderPass = new RenderPass(this.scene, this.camera, undefined, new THREE.Color(0, 0, 0), 0);
    this.bloomComposer.addPass(renderPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(1, 1),
      cfg.strength,
      cfg.radius,
      cfg.threshold,
    );
    this.bloomComposer.addPass(this.bloomPass);

    // Additive overlay quad sampling the bloom result.
    this.overlayMat = new THREE.ShaderMaterial({
      // `tBloom` is (re)bound to the composer's ACTUAL output buffer each frame in `render()` —
      // neither RenderPass nor UnrealBloomPass swaps, so the result lands in `readBuffer`, and
      // caching a fixed target here would sample an empty buffer (= no glow at all).
      uniforms: {
        tBloom: { value: null as THREE.Texture | null },
        uIntensity: { value: cfg.overlay },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: /* glsl */ `
        uniform sampler2D tBloom;
        uniform float uIntensity;
        varying vec2 vUv;
        void main() { gl_FragColor = vec4(texture2D(tBloom, vUv).rgb * uIntensity, 1.0); }`,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.overlayMat);
    quad.frustumCulled = false;
    this.overlayScene.add(quad);

    this.setSize(sizes);
  }

  /**
   * Heavy subtrees to hide during the bloom pass — PURELY a CPU optimisation, with no effect on
   * the output. Even with the camera layer-masked, `renderer.render` still WALKS the whole scene
   * graph and frustum-culls every object; with ~900 cloud sprites plus the grass patches that
   * traversal is the dominant cost of this pass. Three skips an invisible object AND its entire
   * subtree during that walk, so hiding these (none of which are on the bloom layer anyway) makes
   * the pass massively cheaper while rendering exactly the same pixels.
   */
  setTraversalSkip(objects: THREE.Object3D[]): void {
    this.skip = objects.filter(Boolean);
  }

  /** Render the bloom and add it over the current screen. Call AFTER the main direct render. */
  render(): void {
    const cfg = settings.selectiveBloom;
    if (!cfg.enabled) return;

    this.applySettings();

    for (let i = 0; i < this.skip.length; i += 1) {
      this.skipVisible[i] = this.skip[i]!.visible;
      this.skip[i]!.visible = false;
    }

    // Bloom source = the emitters lit normally, every OTHER mesh painted BLACK.
    //
    // WHY NOT JUST A CAMERA LAYER MASK (which is what this did until 2026-07-19): masking to
    // BLOOM_LAYER means the occluding geometry is never submitted, so the bloom buffer has no
    // depth to test against. An emitter BEHIND a stone still drew at full brightness, and the
    // additive composite then laid that glow over the stone in front of it — which read exactly
    // as "when the emission triggers, the other stones go transparent" (user, 2026-07-19).
    //
    // Painting the occluders black instead keeps them in the depth buffer while contributing
    // nothing to the glow (black adds nothing under AdditiveBlending), so hidden emitters are
    // correctly masked. The sky/baked textures still cannot be affected: they are drawn BLACK
    // into an offscreen buffer that is only ever ADDED to the frame, never sampled from it.
    const prevBackground = this.scene.background;
    this.scene.background = null;
    // try/finally is LOAD-BEARING: `darken` leaves every mesh wearing a stand-in material, and
    // anything thrown by the nested render (a water surface's `onBeforeRender`, a shader failure)
    // would otherwise skip `restore()` and strand the WHOLE SCENE in black stand-ins. Every later
    // frame then throws too, because systems like `WaterFX.update` reach into uniforms that are no
    // longer on the mesh — which presents as the app freezing outright (2026-07-19, user: "camera
    // is not animating and nothing is working"). Restore no matter what.
    try {
      this.darken();
      this.bloomComposer.render();
    } finally {
      this.restore();
      this.scene.background = prevBackground;
      for (let i = 0; i < this.skip.length; i += 1) {
        this.skip[i]!.visible = this.skipVisible[i]!;
      }
    }

    // Bind whichever buffer the composer actually ended in, then additively lay the glow over
    // the already-rendered frame (autoClear off so the base frame survives).
    this.overlayMat.uniforms.tBloom!.value = this.bloomComposer.readBuffer.texture;
    this.renderer.setRenderTarget(null);
    const prevAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = false;
    this.renderer.render(this.overlayScene, this.overlayCamera);
    this.renderer.autoClear = prevAutoClear;
  }

  /** Swap every NON-emissive visible mesh to a black stand-in so it occludes without glowing.
   *  Emitters (anything opted into {@link BLOOM_LAYER}) keep their real material. */
  private darken(): void {
    this.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh || !mesh.visible) return;
      if (mesh.layers.test(this.bloomLayers)) return; // an emitter — render it for real
      // HIDE (don't darken) any mesh that hooks `onBeforeRender`. That callback runs DURING the
      // bloom render and typically drives its own shader's uniforms — `WaterComplex.updateFlow`
      // reads `material.uniforms.config` — so with a black stand-in in place it throws mid-pass.
      // Hiding is safer than merely leaving it alone: `threshold` can be 0, so an untouched water
      // surface would bloom its full colour into the glow. It also skips that surface's nested
      // reflection re-render, which has no business running inside a bloom pass.
      if (mesh.onBeforeRender !== THREE.Object3D.prototype.onBeforeRender) {
        this.hidden.push(mesh);
        mesh.visible = false;
        return;
      }
      const current = mesh.material;
      if (!current) return;
      this.saved.set(mesh, current);
      mesh.material = Array.isArray(current)
        ? current.map((m) => this.blackFor(m))
        : this.blackFor(current);
    });
  }

  private restore(): void {
    for (const [mesh, material] of this.saved) mesh.material = material;
    this.saved.clear();
    for (const mesh of this.hidden) mesh.visible = true;
    this.hidden.length = 0;
  }

  /**
   * A black stand-in for one source material, cached per source so this allocates only once.
   *
   * CUTOUT MATERIALS MATTER HERE: the vegetation billboards are alpha-tested quads. A plain
   * opaque black would occlude as a full RECTANGLE, punching square holes in the glow behind
   * foliage — the same class of bug as the cloud depth prepass writing solid quads. Carrying the
   * map + alphaTest over makes them occlude by their real silhouette instead.
   */
  private blackFor(source: THREE.Material): THREE.Material {
    const hit = this.darkCache.get(source.uuid);
    if (hit) return hit;

    const src = source as THREE.Material & {
      map?: THREE.Texture | null;
      alphaTest?: number;
      transparent?: boolean;
      side?: THREE.Side;
    };
    const black = new THREE.MeshBasicMaterial({ color: 0x000000, toneMapped: false, fog: false });
    if (src.side !== undefined) black.side = src.side;
    if (src.map && ((src.alphaTest ?? 0) > 0 || src.transparent)) {
      // `color: black` multiplies the map to black; only the map's ALPHA is used, for the cutout.
      black.map = src.map;
      black.alphaTest = (src.alphaTest ?? 0) > 0 ? src.alphaTest! : 0.5;
    }
    this.darkCache.set(source.uuid, black);
    return black;
  }

  private applySettings(): void {
    const cfg = settings.selectiveBloom;
    this.bloomPass.strength = cfg.strength;
    this.bloomPass.radius = cfg.radius;
    this.bloomPass.threshold = cfg.threshold;
    this.overlayMat.uniforms.uIntensity!.value = cfg.overlay;
  }

  setSize(sizes: SizesState): void {
    // Bloom renders at a FRACTION of the canvas — it's blurry by nature, so half-res is visually
    // free and roughly quarters the fill cost. This is what keeps it usable on weak / software
    // WebGL devices (the user's "make sure this supports CPU things").
    const scale = Math.max(0.15, Math.min(settings.selectiveBloom.resolutionScale, 1));
    const pr = Math.min(sizes.pixelRatio, settings.renderer.maxPixelRatio);
    const w = Math.max(1, Math.floor(sizes.width * pr * scale));
    const h = Math.max(1, Math.floor(sizes.height * pr * scale));
    this.bloomComposer.setSize(w, h);
    this.bloomPass.setSize(w, h);
  }

  dispose(): void {
    this.bloomComposer.dispose();
    this.overlayMat.dispose();
    // The black stand-ins borrow their `map` from the source material — never dispose that here,
    // it is still in use by the real scene.
    for (const material of this.darkCache.values()) material.dispose();
    this.darkCache.clear();
    this.saved.clear();
  }
}
